<template>
  <!-- Sliding panel -->
  <SlidingPanel v-model="slidingPanelIsOpen" :dark="currentPanel === CAMPAIGN_CARD_PANEL" :backgroundImageId="props.modelValue.branding.background_image_id">
    <template #content>
      <div style="padding: 40px; height: 100%;" v-if="currentPanel === BASIC_INFO_PANEL">
        <CampaignConfigPanel :campaign="props.modelValue" @close="closePanel" @save="save"></CampaignConfigPanel>
      </div>
      <div style="padding: 40px; height: 100%;" v-else-if="currentPanel === ENVIRONMENT_PANEL">
        <EnvironmentPanel :campaign="props.modelValue" @close="closePanel" @save="save"></EnvironmentPanel>
      </div>
      <div style="padding: 40px; height: 100%;" v-else-if="currentPanel === EVALUATION_PANEL">
        <EvaluationConfigPanel :campaign="props.modelValue" @close="closePanel" @save="save"></EvaluationConfigPanel>
      </div>
      <div style="padding: 40px; height: 100%;" v-else-if="currentPanel === OPTIONS_PANEL">
        <OptionsConfigPanel :campaign="props.modelValue" @save="save" @close="closePanel" @openCustomizationPanel="openPanel(CAMPAIGN_CARD_PANEL)"></OptionsConfigPanel>
      </div>
      <div style="height: 100%;" v-else-if="currentPanel === CAMPAIGN_CARD_PANEL">
        <CustomizationPanel :campaign="props.modelValue" @save="saveWithoutRunningYuzuAI" @close="closePanel"></CustomizationPanel>
      </div>
    </template>
  </SlidingPanel>
  <div class="field-wrapper">
    <div class="section campaign-recap">
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.recap.infoSection.title') }}</p>
        <Button class="square outline-white" @click="openPanel(BASIC_INFO_PANEL)">
          <template #prefix>
            <GearIcon style="width: 18px"></GearIcon>
          </template>
        </Button>
      </div>
      <div class="campaign-recap-body">
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.chooseSpace.label') }}</p>
          <p>{{ spaceName }}</p>
        </div>
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.campaignName') }}</p>
          <p>{{ props.modelValue.campaign.internal_name }}</p>
        </div>
        <div>
          <p class="light-header">{{ $t('app.campaign.newPage.confirmJob.title') }}</p>
          <p>{{ !props.modelValue.campaign.job_title.length ? $t('app.campaign.newPage.noJob') :  props.modelValue.campaign.job_title }}</p>
        </div>
      </div>
    </div>
    <div class="divider"></div>
    <div class="section environment">
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.recap.environmentSection.title') }}</p>
        <Button class="square outline-white" @click="openPanel(ENVIRONMENT_PANEL)">
          <template #prefix>
            <GearIcon style="width: 18px"></GearIcon>
          </template>
        </Button>
      </div>
      <p>{{ $t(`app.campaign.newPage.slidingPanel.environment.${props.modelValue.assessment.environment}.title`) }}</p>
    </div>
    <div class="divider"></div>
    <div class="section evaluation-informations">
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.recap.evaluationSection.title') }}</p>
        <div :style="{display: 'inline-block'}" class="yuzu-icon yuzu-ai"></div>
        <Button class="square outline-white" @click="openPanel(EVALUATION_PANEL)">
          <template #prefix>
            <GearIcon style="width: 18px"></GearIcon>
          </template>
        </Button>
      </div>
      <div class="skill-card-list">
        <SkillsCard 
          :title="$t('app.campaign.recap.skillCard.required')" 
          :skills="requiredSkillsLabels"
          coefficient="coeff. 3"
        />
        <SkillsCard 
          :title="$t('app.campaign.recap.skillCard.important')" 
          :skills="importantSkillsLabels"
          coefficient="coeff. 2"
        />
        <SkillsCard 
          :title="$t('app.campaign.recap.skillCard.secondary')" 
          :skills="secondarySkillsLabels"
          coefficient="coeff. 1"
        />
        <SkillsCard 
          :title="$t('app.campaign.recap.skillCard.ignored')" 
          :skills="ignoredSkillsLabels"
        />
      </div>
      <p>{{ $t('app.campaign.newPage.recap.evaluationSection.adventureDurationText') }}&nbsp;<span class="bold">{{ skillsStore.getAdventureDuration(props?.modelValue?.assessment?.modules || []) }} minutes</span></p>      
    </div>
    <div class="divider"></div>
    <div class="section additional-options">
      <div class="section-title">
        <p>{{ $t('app.campaign.newPage.recap.optionsSection.title') }}</p>
        <Button class="square outline-white" @click="openPanel(OPTIONS_PANEL)">
          <template #prefix>
            <GearIcon style="width: 18px"></GearIcon>
          </template>
        </Button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.field-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  .section {
    .section-title {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 16px;

      p {
        color: var(--secondary-color);
        font-family: $bioSans;
        font-size: 20px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
      }
    }

    .light-header {
      color: var(--secondary-color);
      font-family: $bioSans;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 24px; /* 150% */
    }

    .campaign-recap-body {
      display: flex;
      justify-content: space-between;
      align-items: center;

    }
  }

  .skill-card-list {
    margin: 8px 0;
    display: flex;
    gap: 10px;
    align-items: center;

    .skills-card {
      flex: 1;
      align-self: stretch;
      min-height: 150px;
    }
  }

  .divider {
    height: 1px;
    background-color: var(--light-grey-color);
    width: 100%;
  }
}
</style>

<script setup lang="ts">
import { cloneDeep } from "lodash";

import SlidingPanel from '../SlidingPanel.vue';
import CampaignConfigPanel from '../configPanels/CampaignConfigPanel.vue';
import EnvironmentPanel from '../configPanels/EnvironmentPanel.vue';
import EvaluationConfigPanel from '../configPanels/EvaluationConfigPanel.vue';
import OptionsConfigPanel from '../configPanels/OptionsConfigPanel.vue';
import CustomizationPanel from '../configPanels/CustomizationPanel.vue';
import SkillsCard from '@/components/global/SkillsCard.vue';

import GearIcon from '@/assets/images/icon/campaign/gear.svg?component';

import { CampaignCreation, WeightingModule } from '@/types/campaign';
import { useSpaces } from "@/queries/space.query";

const BASIC_INFO_PANEL = 0;
const ENVIRONMENT_PANEL = 1;
const EVALUATION_PANEL = 2;
const OPTIONS_PANEL = 3;
const CAMPAIGN_CARD_PANEL = 4;

const currentPanel = ref(BASIC_INFO_PANEL);

const slidingPanelIsOpen = ref(false);

const route = useRoute();
const skillsStore = useSkillsStore();
const { skills } = useSkillsStore();

const { t } = useI18n();

const { data: spaces, isPending: isSpacesPending } = useSpaces();

const props = defineProps<{
  modelValue: CampaignCreation,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: CampaignCreation): void
  (e: 'valid', value: boolean): void
  (e: 'runYuzuAi'): void
  (e: 'configPanelIsOpen', value: boolean): void
}>();

const spaceName = computed(() => {
  if (isSpacesPending.value) return "";
  const space = spaces.value.find(space => space.workspace_id === props.modelValue.campaign.parent_workspace_id);
  return space?.name || "";
});

const requiredSkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  return props.modelValue.assessment.weighting?.required || [];
});

const importantSkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  const important = cloneDeep(props.modelValue.assessment.weighting?.primary || []);
  skills.filter(s => s.type === "style" && !s.disabled).forEach((skill) => {
    if (props?.modelValue?.assessment?.modules?.includes(skill.id as WeightingModule) && !important.includes(skill.id as WeightingModule)) {
      important.push(skill.id as WeightingModule);
    }
  });
  return important;
});

const secondarySkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  return props.modelValue.assessment.weighting?.secondary || [];
});

const ignoredSkillsLabels = computed(() => {
  if (typeof props.modelValue.assessment.weighting === 'string') return [];
  const ignored = cloneDeep(props.modelValue.assessment.weighting?.ignored || []);

  skills.filter(s => s.type === "style" && !s.disabled).forEach((skill) => {
    if (!props?.modelValue?.assessment?.modules?.includes(skill.id as WeightingModule) && !ignored.includes(skill.id as WeightingModule)) {
      ignored.push(skill.id as WeightingModule);
    }
  });
  return ignored;
});

function openPanel(panel: number) {
  currentPanel.value = panel;
  slidingPanelIsOpen.value = true;
  emit('configPanelIsOpen', true);
}

function closePanel() {
  slidingPanelIsOpen.value = false;
  emit('configPanelIsOpen', false);
}


function saveWithoutRunningYuzuAI(value: CampaignCreation) {
  emit('update:modelValue', value);
}

function save(value: CampaignCreation) {
  if (value.campaign.job_title.trim() !== props.modelValue.campaign.job_title.trim()) {
    emit('update:modelValue', value);
    emit('runYuzuAi');
  } else {
    emit('update:modelValue', value);
  }
}

</script>